import { DestroyRef, Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';
import { captureException } from '@sentry/angular';
import { InvoicesGateway } from '@wilson/api/gateway';
import { InvoiceDTO } from '@wilson/invoicing/interfaces';
import { Currency } from '@wilson/localization';
import { GridApi } from 'ag-grid-community';
import { NzMessageService } from 'ng-zorro-antd/message';
import {
  Subject,
  catchError,
  debounceTime,
  distinctUntilChanged,
  map,
  of,
  switchMap,
  tap,
} from 'rxjs';

/**
 * Shape of the pinned bottom row. The net and gross cells reuse the regular
 * column renderers, which read `netSum`, `totalSum` and `currency` off the row
 * data — hence the field names. `id` is deliberately absent so the cell click
 * handlers, which bail out on a missing id, cannot navigate from this row.
 */
export interface InvoicePageTotalRow {
  isPageTotal: true;
  pageTotalLabel: string;
  netSum: string;
  totalSum: string;
  currency: Currency;
}

/**
 * Reserves the pinned-bottom row's height from grid creation, before the real
 * total has loaded. Without it, `initialState.scroll` gets clamped against a
 * taller (footer-less) viewport and never re-maximised once the real row
 * lands — permanently hiding the last row by one row's height (WILP-13180).
 */
export const EMPTY_PAGE_TOTAL_ROW: InvoicePageTotalRow = {
  isPageTotal: true,
  pageTotalLabel: '',
  netSum: '',
  totalSum: '',
  currency: Currency.EUR,
};

/**
 * Keeps the invoices overview's pinned bottom row in sync with the totals of
 * the current page (WILP-12866).
 *
 * The totals come from POST /v2/invoices/summary — never from summing the
 * column values client side. The ids are taken from the grid's post
 * filter-and-sort model rather than the rendered rows, because the grid
 * virtualises rows and rendered rows would cover only the viewport.
 */
@Injectable()
export class InvoicePageTotalsService {
  private readonly invoicesGateway = inject(InvoicesGateway);
  private readonly translate = inject(TranslateService);
  private readonly message = inject(NzMessageService);
  private readonly destroyRef = inject(DestroyRef);

  /** Collapses the burst of grid events a single page change produces. */
  static readonly REFRESH_DEBOUNCE_MS = 200;

  /** Request cap of POST /v2/invoices/summary; a larger page cannot be summed. */
  static readonly MAX_IDS = 1000;

  connect(api: GridApi): void {
    const refresh$ = new Subject<void>();
    const onGridChange = (): void => refresh$.next();

    // paginationChanged covers page navigation and page-size changes;
    // modelUpdated covers new row data after a filter change and re-sorting,
    // which both change which invoices sit on the current page.
    api.addEventListener('paginationChanged', onGridChange);
    api.addEventListener('modelUpdated', onGridChange);

    this.destroyRef.onDestroy(() => {
      api.removeEventListener('paginationChanged', onGridChange);
      api.removeEventListener('modelUpdated', onGridChange);
      refresh$.complete();
    });

    refresh$
      .pipe(
        debounceTime(InvoicePageTotalsService.REFRESH_DEBOUNCE_MS),
        map(() => this.currentPageInvoices(api)),
        distinctUntilChanged(
          (previous, current) => this.idsKey(previous) === this.idsKey(current),
        ),
        // Drop the previous total the moment a new request starts, so a slow
        // response can never leave a total belonging to another page on screen.
        tap(() => this.setPinnedRow(api, null)),
        switchMap((invoices) => {
          if (
            invoices.length === 0 ||
            invoices.length > InvoicePageTotalsService.MAX_IDS
          ) {
            return of(null);
          }

          return this.invoicesGateway
            .getInvoicesSummary(invoices.map((invoice) => invoice.id))
            .pipe(
              map((totals) => this.toPinnedRow(totals, invoices)),
              catchError((error) => {
                captureException(error);
                this.message.error(
                  this.translate.instant('general.something_went_wrong'),
                );
                return of(null);
              }),
            );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((row) => this.setPinnedRow(api, row));

    refresh$.next();
  }

  private currentPageInvoices(api: GridApi): InvoiceDTO[] {
    const pageSize = api.paginationGetPageSize();
    const firstIndexOnPage = api.paginationGetCurrentPage() * pageSize;
    const lastIndexOnPage = firstIndexOnPage + pageSize;
    const invoices: InvoiceDTO[] = [];

    api.forEachNodeAfterFilterAndSort((node, index) => {
      const invoice = node.data as InvoiceDTO | undefined;
      if (index >= firstIndexOnPage && index < lastIndexOnPage && invoice?.id) {
        invoices.push(invoice);
      }
    });

    return invoices;
  }

  private toPinnedRow(
    totals: { netSum: string; totalSum: string },
    invoices: InvoiceDTO[],
  ): InvoicePageTotalRow {
    return {
      isPageTotal: true,
      pageTotalLabel: this.translate.instant('page.invoicing.page_total'),
      netSum: totals.netSum,
      totalSum: totals.totalSum,
      currency: this.pageCurrency(invoices),
    };
  }

  /**
   * The endpoint returns bare amounts, so the row needs a currency for the
   * locale-aware formatting. The page's first invoice decides it; a page mixing
   * currencies would be mislabelled, which the backend contract cannot express.
   */
  private pageCurrency(invoices: InvoiceDTO[]): Currency {
    const currency = invoices[0]?.currency as Currency | undefined;
    return currency && currency in Currency ? currency : Currency.EUR;
  }

  /**
   * Always keeps exactly one pinned row, blanked to EMPTY_PAGE_TOTAL_ROW
   * rather than removed when there's nothing to total. Dropping to zero
   * pinned rows changes the grid viewport's height, which clamps an
   * in-progress scroll restore and never recovers (WILP-13180).
   */
  private setPinnedRow(api: GridApi, row: InvoicePageTotalRow | null): void {
    api.setGridOption('pinnedBottomRowData', [row ?? EMPTY_PAGE_TOTAL_ROW]);
  }

  private idsKey(invoices: InvoiceDTO[]): string {
    return invoices.map((invoice) => invoice.id).join(',');
  }
}
